<template>
  <div class="custom-select-datepicker">
    <label
      v-if="accessibleLabel"
      :id="dateRangeLabelId"
      :for="dateRangeInputId"
      class="v3-form-label d-block mb-1"
      >{{ accessibleLabel }}</label
    >
    <CustomSelect
      v-if="!state.showDatepicker"
      v-model="state.selectedRange"
      :input-id="dateRangeInputId"
      :labelled-by="dateRangeLabelId"
      hide-floating-label
      :accessible-label="accessibleLabel"
      @update:model-value="selectOption"
      :filterable="false"
      :searchable="false"
      :close-on-select="true"
      :options="dateRanges"
      placeholder="Select Date Range"
      class="custom-select-datepicker-date-select"
      :append-icon="'ri-calendar-line'"
    >
    </CustomSelect>

    <CustomDatePicker
      v-if="state.showDatepicker"
      :is-range="true"
      :input-id="dateRangeInputId"
      :label-id="dateRangeLabelId"
      :min-date="minDate"
      :max-date="maxDate"
      :placeholder="'Select Date Range'"
      :is-custom-select="state.showDatepicker"
      v-model="state.selectedRangeCalendar"
      @update:model-value="onCloseCalendar"
    >
    </CustomDatePicker>
  </div>
</template>
<script>
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomDatePicker from "@/v3components/shared/Form/CustomDatePicker.vue"
import { reactive, computed, watch } from "vue"
import helpers from "@/helpers/index"
import moment from "moment-timezone"

export default {
  name: "VisitorLogDateRangePicker",
  components: { CustomSelect, CustomDatePicker },
  emits: ["update:modelValue"],
  props: {
    dateRanges: Array,
    modelValue: String,
    /** Visible label for the date range selector (WCAG 3.3.2). */
    accessibleLabel: {
      type: String,
      default: ""
    },
    limitMaxDate: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const uid = Math.random().toString(36).slice(2, 11)
    const dateRangeInputId = `visitor-log-date-range-${uid}`
    const dateRangeLabelId = `visitor-log-date-range-lbl-${uid}`

    const state = reactive({
      showDatepicker: false,
      selectedRange: props.dateRanges ? props.dateRanges[2].range : "",
      selectedRangeCalendar: null,
      startDate: null,
      endDate: null
    })

    const minDate = computed(() => {
      // Visitor logs are historical: let admins reach the previous school year so
      // months before the current school year start remain selectable.
      const current = helpers.minSchoolYearDate()
      return new Date(
        current.getFullYear() - 1,
        current.getMonth(),
        current.getDate()
      )
    })

    const maxDate = computed(() => {
      // Logs cannot exist in the future, so cap selection at today.
      return new Date(helpers.currTzDate(moment()))
    })

    watch(
      () => props.modelValue,
      (modelValueRange) => {
        if (modelValueRange) {
          state.selectedRange = modelValueRange.range
            ? modelValueRange.range
            : modelValueRange
          if (modelValueRange.id === "custom") {
            state.selectedRangeCalendar = {
              start: modelValueRange.startDate
                ? modelValueRange.startDate
                : null,
              end: modelValueRange.endDate ? modelValueRange.endDate : null
            }
            if (
              modelValueRange.current_school_year &&
              modelValueRange.current_school_year != modelValueRange.range
            ) {
              state.showDatepicker = true
            } else {
              state.showDatepicker = false
            }
            state.startDate = modelValueRange.startDate
              ? modelValueRange.startDate
              : null
            state.endDate = modelValueRange.endDate
              ? modelValueRange.endDate
              : null
          }
        }
      }
    )

    const selectOption = () => {
      if (state.selectedRange.id === "custom") {
        state.startDate = state.selectedRange.startDate
          ? state.selectedRange.startDate
          : null
        state.endDate = state.selectedRange.endDate
          ? state.selectedRange.endDate
          : null
        state.showDatepicker = true
        state.selectedRangeCalendar = {
          start: moment()._d,
          end: moment()._d
        }
      } else {
        setRange(false)
      }
    }

    const setRange = (isFromCalendar) => {
      let value

      if (isFromCalendar) {
        const range =
          helpers.currTzDate(state.selectedRangeCalendar.start) +
          " - " +
          helpers.currTzDate(state.selectedRangeCalendar.end)

        value = range
        state.selectedRange = range
        // A range picked from the calendar is always within [minDate, maxDate],
        // so return to the range selector once the selection is applied.
        state.showDatepicker = false
      } else {
        if (state.selectedRange.id !== "custom") {
          value = state.selectedRange.range
        } else {
          value = state.selectedRange
        }
        state.selectedRange = state.selectedRange.range
      }

      emit("update:modelValue", value)
    }
    const onCloseCalendar = () => {
      setRange(true)
    }

    return {
      state,
      minDate,
      maxDate,
      dateRangeInputId,
      dateRangeLabelId,
      setRange,
      onCloseCalendar,
      selectOption
    }
  }
}
</script>
